// Typing cells on a phone (design 02 9): which keyboard a cell gets, where
// the keyboard's "다음" goes, the cell references a tap puts into a formula
// (Excel's point mode), and the order of the number boxes of a tensor's
// table (다음 goes down a column, then to the top of the next).

/** The phone's keyboard: number keys or letters. */
export type Keys = 'num' | 'text'

/** Where 다음 goes from a cell. */
export type Dir = 'down' | 'right'

/** A number as typed (sign, digits, grouping commas, a point, %). */
export function isPlainNumber(input: string): boolean {
  return /^\s*[+-]?(\d[\d,]*(\.\d*)?|\.\d+)\s*%?\s*$/.test(input)
}

/**
 * The keyboard for typing into a cell: by what it holds, else by what the
 * cell before it holds (above it when 다음 goes down, left of it when it
 * goes right). Numbers get the number keys; text, formulas, dates and an
 * empty start get the letters.
 */
export function keysFor(input: string, before: string): Keys {
  const t = input.trim() !== '' ? input : before
  return isPlainNumber(t) ? 'num' : 'text'
}

/** The step of 다음: (rows, columns). */
export function stepOf(d: Dir): [number, number] {
  return d === 'down' ? [1, 0] : [0, 1]
}

/** A reference a tap put into the formula, kept so the next tap replaces it. */
export interface PointRef {
  from: number
  to: number
  ref: string
}

/**
 * Where a tapped cell's reference goes in the formula being typed, or null
 * when the tap is not for that (the text is not a formula, or the caret
 * does not follow an operator, a parenthesis, a comma, a colon or the
 * leading =). The reference the last tap put in is replaced while the
 * caret is still right after it. [start, end) is the text box's selection.
 */
export function pointAt(text: string, start: number, end: number, last: PointRef | null): { from: number; to: number } | null {
  if (!text.startsWith('=')) return null
  if (last && start === end && end === last.to && text.slice(last.from, last.to) === last.ref) return { from: last.from, to: last.to }
  const before = text.slice(0, start).replace(/\s+$/, '')
  if (!/[=(,:;+\-*/^&<>]$/.test(before)) return null
  return { from: start, to: end }
}

/** Puts ref into text at [from, to): the new text and where the caret goes. */
export function putRef(text: string, at: { from: number; to: number }, ref: string): { text: string; caret: number } {
  return { text: text.slice(0, at.from) + ref + text.slice(at.to), caret: at.from + ref.length }
}

/** The text with the line break just typed before caret taken out (a
    keyboard's 다음 that came as a new line). */
export function dropBreak(text: string, caret: number): string {
  if (caret <= 0 || text[caret - 1] !== '\n') return text
  return text.slice(0, caret - (text[caret - 2] === '\r' ? 2 : 1)) + text.slice(caret)
}

/**
 * The box after `at` (before it with back) among a table's number boxes:
 * down the column, then on at the top of the next column. Index into
 * places, or -1 at the end.
 */
export function nextPlace(places: readonly { r: number; c: number }[], at: { r: number; c: number }, back = false): number {
  const order = (p: { r: number; c: number }, q: { r: number; c: number }) => p.c - q.c || p.r - q.r
  let best = -1
  places.forEach((p, i) => {
    const d = order(p, at)
    if (back ? d >= 0 : d <= 0) return
    const e = best < 0 ? 0 : order(p, places[best]!)
    if (best < 0 || (back ? e > 0 : e < 0)) best = i
  })
  return best
}
